import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { EXTENSIONS } from "@/lib/extensions";
import { Field, missingRequired } from "@/components/FormFields";
import { StatusPill } from "@/pages/Profile";
import { visible } from "@/lib/extensions";
import { toast } from "sonner";
import { ArrowLeft, Send } from "lucide-react";

export default function ExtensionForm() {
  const { type } = useParams();
  const { t, lang } = useLang();
  const a = t.account;
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const ext = EXTENSIONS[type];
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("none");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/profile/extensions").then((r) => {
      const e = r.data.find((x) => x.type === type);
      setData(e?.data || {}); setStatus(e?.status || "none"); setNote(e?.review_note || "");
    });
  }, [type]);

  if (!ext) return <div className="p-20 text-center">404</div>;
  if (data === null) return <div className="max-w-3xl mx-auto px-6 py-20"><div className="h-64 bg-slate-50 animate-pulse rounded-2xl" /></div>;

  const locked = status === "pending_review";
  const missing = missingRequired(ext.sections, data);

  const saveDraft = async () => {
    setBusy(true);
    try { await api.put(`/profile/extensions/${type}`, { data }); setStatus((s) => (s === "none" ? "draft" : s)); toast.success(a.draftSaved); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const submit = async () => {
    if (missing.length) { toast.error(`${a.required}: ${missing.map((f) => f.l[lang]).join(", ")}`); return; }
    setBusy(true);
    try {
      await api.put(`/profile/extensions/${type}`, { data });
      const { data: res } = await api.post(`/profile/extensions/${type}/submit`);
      setStatus(res.status);
      await refreshMe();
      toast.success(res.status === "active" ? a.activated : a.sentReview);
      navigate("/perfil");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14 lg:py-20" data-testid={`ext-form-${type}`}>
      <Link to="/perfil" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B192C]" data-testid="ext-back"><ArrowLeft className="h-4 w-4" /> {a.title}</Link>
      <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">{a.extension}</p>
          <h1 className="font-serif text-4xl lg:text-5xl mt-3 text-[#0B192C]" data-testid="ext-title">{ext.title[lang]}</h1>
        </div>
        <StatusPill status={status} lang={lang} testid="ext-current-status" />
      </div>
      <p className="mt-4 text-slate-500 max-w-xl">{ext.desc[lang]}</p>
      <p className="mt-2 text-xs text-slate-400 font-display">{a.noRepeat}</p>
      {status === "rejected" && note && <p className="mt-4 text-sm text-red-700 bg-red-50 rounded-xl p-4" data-testid="ext-review-note">{a.rejectedNote}: {note}</p>}
      {locked && <p className="mt-4 text-sm text-blue-700 bg-blue-50 rounded-xl p-4" data-testid="ext-locked-note">{a.pendingNote}</p>}

      <div className={`mt-12 space-y-12 ${locked ? "opacity-60 pointer-events-none" : ""}`}>
        {ext.sections.map((s) => visible(s.showIf, data) && (
          <section key={s.title.es} className="card-clean p-8 space-y-6" data-testid={`ext-section`}>
            <h2 className="font-serif text-2xl text-[#0B192C]">{s.title[lang]}</h2>
            {s.fields.map((f) => <Field key={f.k} f={f} data={data} setData={setData} lang={lang} prefix="ext" />)}
          </section>
        ))}
      </div>

      {!locked && (
        <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
          <button onClick={saveDraft} disabled={busy} className="btn-ghost text-sm" data-testid="ext-save-draft">{a.saveDraft}</button>
          <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-40" data-testid="ext-submit">
            <Send className="h-4 w-4" /> {status === "active" ? a.update : ext.submitLabel[lang]}
          </button>
        </div>
      )}
      {ext.review && !locked && <p className="mt-4 text-xs text-slate-400">{a.reviewInfo}</p>}
    </div>
  );
}
